import {
  IconName,
  useCurrenciesFilter,
  useGenericFilter,
  type ClientSideFilterRule,
  type FilterableProperty,
  type FixingIndex,
  type UsePersistedBlotterTable,
} from '@talos/kyoko';
import { useMemo } from 'react';

export interface FixingIndicesFilter {
  IndexID?: string[];
  Asset?: string[];
  QuoteCurrency?: string[];
  ValueType?: string[];
}

interface UseFixingIndicesFilterParams {
  persistedBlotterTable: UsePersistedBlotterTable;
}

const FIXING_INDICES_FILTER_RULES: ClientSideFilterRule<FixingIndicesFilter>[] = [
  {
    type: 'include',
    filterKey: 'IndexID',
    dataKey: 'IndexID',
  },
  {
    type: 'include',
    filterKey: 'Asset',
    dataKey: 'Asset',
  },
  {
    type: 'include',
    filterKey: 'QuoteCurrency',
    dataKey: 'QuoteCurrency',
  },
  {
    type: 'include',
    filterKey: 'ValueType',
    dataKey: 'ValueType',
  },
];

export function useFixingIndicesFilter({ persistedBlotterTable }: UseFixingIndicesFilterParams) {
  const currenciesFilter = useCurrenciesFilter();

  const filterableProperties = useMemo(
    () =>
      [
        {
          key: 'IndexID',
          label: 'Index ID',
          icon: IconName.Hashtag,
          control: 'text',
        },
        {
          ...currenciesFilter,
          key: 'Asset',
          label: 'Asset',
        },
        {
          ...currenciesFilter,
          key: 'QuoteCurrency',
          label: 'Quote Currency',
        },
        {
          key: 'ValueType',
          label: 'Value Type',
          icon: IconName.CheckCircle,
          options: ['Price', 'Rate'] satisfies FixingIndex['ValueType'][],
          getOptionLabel: (option: string) => option,
        },
      ] satisfies FilterableProperty[],
    [currenciesFilter]
  );

  return useGenericFilter<FixingIndicesFilter>(filterableProperties, {
    initialFilter: persistedBlotterTable.initialState?.filter,
    onFilterChanged: persistedBlotterTable.onFilterChanged,
    clientSideFilterRules: FIXING_INDICES_FILTER_RULES,
  });
}

const colIDToFilterBuilderKeyMap = {
  IndexID: 'IndexID',
  Asset: 'Asset',
  QuoteCurrency: 'QuoteCurrency',
  ValueType: 'ValueType',
} as const satisfies Partial<Record<keyof FixingIndex, keyof FixingIndicesFilter>>;

export function colIDToFilterBuilderKey(id: string): keyof FixingIndicesFilter | undefined {
  return colIDToFilterBuilderKeyMap[id as keyof typeof colIDToFilterBuilderKeyMap] as
    | keyof FixingIndicesFilter
    | undefined;
}
